/**
 * Formatting helpers — time labels, date separators, last-seen, avatar colors.
 * Uses date-fns per 06 §0.
 */

import { format, isToday, isYesterday, differenceInCalendarDays } from 'date-fns';

/** Short clock time inside bubbles / list rows (e.g. "10:42"). */
export function clockTime(ms: number): string {
  return format(ms, 'HH:mm');
}

/** Chat-list timestamp: time today, "Yesterday", weekday <7d, else date. */
export function listTime(ms: number): string {
  const d = new Date(ms);
  if (isToday(d)) return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Yesterday';
  if (differenceInCalendarDays(new Date(), d) < 7) return format(d, 'EEEE');
  return format(d, 'dd/MM/yyyy');
}

/** Date separator label (06 §4.5). */
export function dateSeparator(ms: number): string {
  const d = new Date(ms);
  if (isToday(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  if (differenceInCalendarDays(new Date(), d) < 7) return format(d, 'EEEE');
  return format(d, 'dd/MM/yyyy');
}

/** Presence subtitle (06 §4.8), privacy already applied upstream. */
export function lastSeenText(state: 'online' | 'offline' | 'unknown', lastSeenAtMs: number | null): string {
  if (state === 'online') return 'online';
  if (state === 'unknown' || lastSeenAtMs == null) return '';
  const d = new Date(lastSeenAtMs);
  if (isToday(d)) return `last seen today at ${format(d, 'HH:mm')}`;
  if (isYesterday(d)) return `last seen yesterday at ${format(d, 'HH:mm')}`;
  return `last seen ${format(d, 'dd/MM/yyyy')}`;
}

/** Two-letter initials from a display name. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] as string).slice(0, 2).toUpperCase();
  return ((parts[0] as string)[0]! + (parts[parts.length - 1] as string)[0]!).toUpperCase();
}

/** WhatsApp-style 18-color deterministic avatar/sender palette (06 §4.3, §4.13). */
const PALETTE = [
  '#dd5044', '#e8730c', '#bf9000', '#5d9c19', '#1f9b6c', '#0a7d7d',
  '#0c8de8', '#2b6fd6', '#5b51d8', '#8e44ad', '#c2389a', '#d63864',
  '#7a6f63', '#1a8e6e', '#b5651d', '#3b7a57', '#9b2d30', '#2c6e91',
];

export function colorForId(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(hash) % PALETTE.length] as string;
}

/** True when message body is 1–3 emoji only → jumbo render (06 §3.2). */
export function isEmojiJumbo(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  const noEmoji = t.replace(
    /[\p{Extended_Pictographic}‍️\u{1F3FB}-\u{1F3FF}]/gu,
    '',
  );
  if (noEmoji.trim().length > 0) return false;
  const segs = [...new Intl.Segmenter('en', { granularity: 'grapheme' }).segment(t)];
  return segs.length <= 3;
}
